import React, { useState, useMemo } from 'react';
import { Star, CheckCircle, Shield, User, ArrowRight, ArrowLeft, X, Loader, AlertTriangle } from 'lucide-react';
import VerificationSteps from "./VerificationSteps";
import { OptimizedDropdown } from './OptimizedDropdown';
import { EnhancedUserDropdown } from './EnhancedUserDropdown';
import unifiedAuthService from '../services/unifiedAuth';
import { serviceTypes, categories, regions, reviewTypes, mockServices } from '../constants';

import ProfileImage from './ProfileImage';

// Centralized API base URL for backend Express server
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071';

// Copy Button Component with visual feedback
const CopyButton = ({ code }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className={`text-sm transition-colors underline ${copied
        ? 'text-green-600 hover:text-green-800'
        : 'text-blue-600 hover:text-blue-800'
        }`}
    >
      {copied ? '✅ Code copied!' : '📋 Copy code to clipboard'}
    </button>
  );
};

// Simple in-memory cache for user lookups (production-ready)
const userCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

const getCachedUser = (username) => {
  const cached = userCache.get(username.toLowerCase());
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  return null;
};

const setCachedUser = (username, userData) => {
  userCache.set(username.toLowerCase(), {
    data: userData,
    timestamp: Date.now()
  });
};

const isDemoUser = (username) => {
  const demoUsernames = ['@john_doe', '@sarah_fitness', '@business_owner', '@startup_founder'];
  return demoUsernames.includes(username.toLowerCase());
};

export const QuickReviewFlow = ({
  onClose,
  selectedService,
  // serviceTypes: propServiceTypes,
  onComplete,
  isLoggedIn = false,
  userProfile = null,
  setUserProfile,
  setVerificationNotification, // <-- Add this prop
  preSelectedBusiness = null // <-- New prop to skip Case 1
}) => {
  // Add safety check for userProfile
  const safeUserProfile = useMemo(() => {
    if (!userProfile || typeof userProfile !== 'object') return null;

    try {
      console.log('🔍 Original userProfile structure:', userProfile);

      // Ensure identifiers is an object with string values
      const identifiers = userProfile.identifiers || {};
      console.log('🔍 Original identifiers:', identifiers);

      const safeIdentifiers = {};

      // Safely extract string values, converting objects to strings if needed
      Object.keys(identifiers).forEach(key => {
        const value = identifiers[key];
        if (typeof value === 'string') {
          safeIdentifiers[key] = value;
        } else if (value && typeof value === 'object') {
          // If it's an object, try to extract a meaningful string
          if (value.email && typeof value.email === 'string') {
            safeIdentifiers[key] = value.email;
          } else if (value.username && typeof value.username === 'string') {
            safeIdentifiers[key] = value.username;
          } else if (value.identifier && typeof value.identifier === 'string') {
            safeIdentifiers[key] = value.identifier;
          } else {
            // Convert object to string as last resort
            safeIdentifiers[key] = JSON.stringify(value);
          }
        } else if (value !== null && value !== undefined) {
          // Convert other types to string
          safeIdentifiers[key] = String(value);
        }
      });

      console.log('🔍 Safe identifiers:', safeIdentifiers);

      return {
        ...userProfile,
        identifiers: safeIdentifiers
      };
    } catch (error) {
      console.error('Error processing userProfile:', error);
      return null;
    }
  }, [userProfile]);

  const [currentStep, setCurrentStep] = useState(preSelectedBusiness ? 2 : 1);
  const [isLookingUpUser, setIsLookingUpUser] = useState(false);
  const [availableBusinesses, setAvailableBusinesses] = useState([]);
  const [lookupError, setLookupError] = useState(null);
  const [businessService, setBusinessService] = useState(preSelectedBusiness || null);
  const [isSavingReview, setIsSavingReview] = useState(false);
  const [reviewSaveSuccess, setReviewSaveSuccess] = useState(false);
  // Track verification state separately
  const [verificationStatus, setVerificationStatus] = useState({
    isVerified: false,
    authToken: null,
    method: null,
    userId: null,
    userProfile: null
  });

  const [reviewData, setReviewData] = useState({
    user: preSelectedBusiness || null,
    rating: 0,
    reviewType: null,
    serviceType: null,
    region: null,
    comment: '',
    // New verification fields
    verifyUsername: null,
    // Verification method selection
    verificationMethod: null, // 'instagram', 'phone', 'email'
    // Phone verification
    phoneNumber: '',
    phoneVerificationCode: '',
    // Email verification
    emailAddress: '',
    emailVerificationCode: ''
  });

  const totalSteps = 8; // Removed verification step - promotion code is the final step

  // Demo user profiles - moved outside of useEffect to avoid dependency warning
  const demoUsers = React.useMemo(() => [
    { id: 1, username: '@john_doe', avatar: 'J', verified: true, label: '@john_doe (Verified)', source: 'demo' },
    { id: 2, username: '@sarah_fitness', avatar: 'S', verified: true, label: '@sarah_fitness (Verified)', source: 'demo' },
    { id: 3, username: '@business_owner', avatar: 'B', verified: false, label: '@business_owner', source: 'demo' },
    { id: 4, username: '@startup_founder', avatar: 'S', verified: true, label: '@startup_founder (Verified)', source: 'demo' }
  ], []);

  // Helper function to check if a user is a demo user
  const isDemoUser = (username) => {
    return demoUsers.some(user => user.username.toLowerCase() === username.toLowerCase());
  };

  // Helper functions for user caching (localStorage)
  const getCachedUser = (username) => {
    try {
      const cached = localStorage.getItem(`user_cache_${username}`);
      if (cached) {
        const data = JSON.parse(cached);
        // Check if cache is less than 24 hours old
        if (Date.now() - data.timestamp < 24 * 60 * 60 * 1000) {
          return data.user;
        }
      }
    } catch (error) {
      console.warn('Error reading user cache:', error);
    }
    return null;
  };

  const setCachedUser = (username, userData) => {
    try {
      localStorage.setItem(`user_cache_${username}`, JSON.stringify({
        user: userData,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.warn('Error setting user cache:', error);
    }
  };

  // Initialize available users with demo users
  React.useEffect(() => {
    setAvailableBusinesses(demoUsers);
  }, [demoUsers]);

  // Sync businessService with reviewData.user
  // React.useEffect(() => {
  //   if (reviewData.user && !businessService) {
  //     setBusinessService(reviewData.user);
  //   }
  // }, [reviewData.user, businessService]);

  // New function to generate verification code with enhanced uniqueness and security
  const generateVerificationCode = (username) => {
    const cleanUsername = username.replace('@', '').toLowerCase().trim();

    // Use last 8 digits of timestamp for better uniqueness (27.8 hour cycle)
    const timestamp = Date.now().toString().slice(-8);

    // Add 4-digit random number for extra security (0000-9999)
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');

    // Optional: Add session entropy for even more uniqueness
    const entropy = Math.floor(Math.random() * 100).toString().padStart(2, '0');

    return `${cleanUsername}_${timestamp}${random}${entropy}`;
  };

  // Search users in database first (via backend API)
  const searchUserInDatabase = async (cleanUsername) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user-search?username=${cleanUsername}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.found) {
          console.log('✅ Found user in database:', data.user);
          return data.user;
        }
      }
      return null;
    } catch (error) {
      console.error('❌ Error searching user in database:', error);
      return null;
    }
  };

  const getBusinessByUsername = async (cleanUsername) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/business-crud?username=${cleanUsername}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.found) {
          // console.log('✅ Found business in database:', data.business.instagramUsername);
          return data.business;
        }
      }
      return null;
    } catch (error) {
      console.error('❌ Error searching user in database:', error);
      return null;
    }
  };


  // Save business to database when explicitly selected (via backend API)
  const saveBusinessToDatabase = async (businessData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/business-crud`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ businessData })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Business saved to database:', data);
        return data.business;
      } else {
        console.warn('⚠️ Failed to save business to database:', await response.text());
      }
    } catch (error) {
      console.error('❌ Error saving business to database:', error);
    }
    return null;
  };

  // Save user to database when explicitly selected (via backend API)
  const saveUserToDatabase = async (userData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user-save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userData })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ User saved to database:', data);
        return data.user;
      } else {
        console.warn('⚠️ Failed to save user to database:', await response.text());
      }
    } catch (error) {
      console.error('❌ Error saving user to database:', error);
    }
    return null;
  };

  // Generate simple 6-digit verification code for phone/email
  const generateSimpleVerificationCode = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  // Save complete review data after verification
  const saveCompleteReview = async () => {
    try {
      let result;
      let response;

      // Create properly structured payload for backend
      const reviewPayload = {
        rating: reviewData.rating,
        comment: reviewData.comment,
        // businessService: reviewData.user || {
        //   username: reviewData.emailAddress || 'anonymous',
        //   id: 'demo-user'
        // },
        user: {
          username: safeUserProfile?.identifiers?.instagramUsername || safeUserProfile?.identifiers?.email || 'anonymous',
          verified: true,
          verificationMethod: 'authenticated',
          emailAddress: safeUserProfile?.identifiers?.email || '',
          phoneNumber: safeUserProfile?.identifiers?.phone || ''
        },
        reviewType: reviewData.reviewType,
        serviceType: reviewData.serviceType,
        region: reviewData.region,
        targetBusiness: businessService || {
          username: 'unknown',
          displayName: null,
          searchData: null
        },
        verification: {
          isVerified: reviewData.isUserVerified || reviewData.isPhoneVerified || reviewData.isEmailVerified || false,
          method: reviewData.verificationMethod || 'email',
          verificationCode: reviewData.verificationCode || '',
          skipped: reviewData.skippedVerification || false,
          emailAddress: reviewData.emailAddress || '',
          phoneNumber: reviewData.phoneNumber || ''
        },
        promotionCode: reviewData.promotionCode || null, // Include promotion code
        metadata: {
          completedAt: new Date().toISOString(),
          userAgent: navigator.userAgent,
          source: 'review-flow-v1'
        }
      };

      console.log('🚀🚀 Saving reviewPayload complete review data:', reviewPayload);

      // Use save-complete-review endpoint for verified/authenticated users
      console.log('💾 Using save-complete-review endpoint for verified/authenticated users');

      console.log('🚀🚀 availableBusinesses.  :', availableBusinesses);



      // Prepare the review data in the format expected by save-complete-review
      // Format review data according to what backend expects
      const completeReviewData = {
        user: {
          username: safeUserProfile?.identifiers?.instagramUsername || safeUserProfile?.identifiers?.email || 'anonymous',
          verified: true,
          verificationMethod: 'authenticated',
          emailAddress: safeUserProfile?.identifiers?.email || '',
          phoneNumber: safeUserProfile?.identifiers?.phone || ''
        },
        verification: {
          isVerified: true,
          method: 'authenticated',
          verificationCode: null,
          skipped: false,
          emailAddress: safeUserProfile?.identifiers?.email || '',
          phoneNumber: safeUserProfile?.identifiers?.phone || ''
        },
        rating: reviewPayload.rating,
        reviewType: {
          name: reviewPayload.reviewType?.name || 'Service',
          id: reviewPayload.reviewType?.id || 'default'
        },
        serviceType: {
          name: reviewPayload.serviceType?.name || 'Personal Training',
          id: reviewPayload.serviceType?.id || 'default',
          category: reviewPayload.serviceType?.category || 'Fitness',
          icon: reviewPayload.serviceType?.icon || '🏋️',
          description: reviewPayload.serviceType?.description || 'One-on-one fitness coaching'
        },
        region: {
          name: reviewPayload.region?.name || 'North America',
          id: reviewPayload.region?.id || 'default'
        },
        comment: reviewPayload.comment || '',
        // targetBusiness: availableBusinesses.searchData,
        targetBusiness: businessService,
        // targetBusiness: {
        //   username: reviewData.user?.instagramUsername || reviewData.user?.username?.replace('@', '') || 'unknown',
        //   displayName: reviewData.user?.displayName || reviewData.user?.searchData?.full_name || reviewData.user?.username || 'unknown',
        //   searchData: reviewData.user?.searchData || null,
        //   avatar: reviewData.user?.profilePicture || reviewData.user?.searchData?.profile_pic_url || null
        // },
        // Additional metadata
        reviewerInfo: {
          userId: reviewPayload.verification?.userId,
          verified: !!reviewPayload.verification?.isVerified,
          verificationMethod: reviewPayload.verification?.method || 'none',
          emailAddress: reviewPayload.verification?.emailAddress || '',
          phoneNumber: reviewPayload.verification?.phoneNumber || ''
        },
        reviewContent: {
          comment: reviewPayload.comment || '',
          reviewType: reviewPayload.reviewType?.name || 'Service',
          serviceType: reviewPayload.serviceType?.name || 'Personal Training',
          region: reviewPayload.region?.name || 'North America'
        },
        metadata: {
          source: 'review-flow-v1',
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          promotionCode: reviewPayload.promotionCode || null
        }
      };

      // Get auth token from unified auth service
      const authToken = unifiedAuthService.getAuthToken();
      console.log('🔑 Auth token status:', authToken ? 'Present' : 'Missing');

      if (!authToken) {
        console.error('❌ No auth token found - user must be verified or logged in');
        throw new Error('Authentication required to submit review');
      }

      // Get current user data
      const currentUser = unifiedAuthService.getCurrentUser();
      console.log('👤 Current user:', currentUser);

      if (!currentUser) {
        throw new Error('User data not found');
      }

      // Update review data with user info
      completeReviewData.user = {
        username: currentUser.identifiers?.instagramUsername || currentUser.identifiers?.email || 'anonymous',
        verified: true,
        verificationMethod: reviewPayload.verification?.method || 'email',
        emailAddress: currentUser.identifiers?.email || '',
        phoneNumber: currentUser.identifiers?.phone || ''
      };

      completeReviewData.reviewerInfo = {
        userId: currentUser.userId,
        verified: true,
        verificationMethod: reviewPayload.verification?.method || 'email',
        emailAddress: currentUser.identifiers?.email || '',
        phoneNumber: currentUser.identifiers?.phone || ''
      };

      console.log('🚀🚀 Saving completeReviewData complete review data:', completeReviewData);

      response = await fetch(`${API_BASE_URL}/api/save-complete-review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(completeReviewData)
      });

      console.log('📡 Submit response status:', response.status, response.statusText);

      // Handle response
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Submit request failed:', response.status, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      try {
        result = await response.json();
        console.log('✅ Submit request successful:', result);

        // Transform response to match expected format
        if (result.success) {
          result = {
            success: true,
            reviewId: result.reviewId || 'unknown',
            message: 'Review saved successfully'
          };
        }
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        if (response.status === 201 || response.status === 200) {
          result = { success: true, reviewId: 'unknown', message: 'Review saved successfully' };
          console.log('✅ Using fallback success response');
        } else {
          throw new Error('Invalid response format from server');
        }
      }

      if (result.success) {
        console.log('✅ Review saved successfully:', result.reviewId);
        return result;
      } else {
        throw new Error(result.error || 'Failed to save review');
      }
    } catch (error) {
      console.error('Save review error:', error);
      throw new Error(error.message || 'Failed to save review');
    }
  };

  // New function to handle user verification lookup with demo user support, database search, and caching
  const handleUserVerificationLookup = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 1) return;

    const cleanUsername = searchTerm.replace('@', '').trim();
    const formattedUsername = `@${cleanUsername}`;

    // Check if it's a demo user first
    if (isDemoUser(formattedUsername)) {
      console.log('✅ Using demo user for verification:', formattedUsername);

      const demoUser = demoUsers.find(user => user.username.toLowerCase() === formattedUsername.toLowerCase());
      if (demoUser) {
        const verifyUser = {
          id: `verify_${demoUser.id}`,
          username: demoUser.username,
          avatar: demoUser.avatar,
          verified: demoUser.verified,
          displayName: demoUser.username,
          profilePicture: null,
          searchData: {
            full_name: demoUser.username,
            username: demoUser.username.replace('@', ''),
            followers: Math.floor(Math.random() * 10000) + 1000,
            posts: Math.floor(Math.random() * 500) + 50,
            is_verified: demoUser.verified,
            is_business_account: false
          }
        };

        // Generate verification code
        const code = generateVerificationCode(demoUser.username.replace('@', ''));

        setReviewData(prev => ({
          ...prev,
          verifyUsername: verifyUser,
          generatedCode: code
        }));

        setLookupError(null);
        return;
      }
    }

    // Search in database first
    console.log('🔍 Searching verification user in database:', cleanUsername);
    const dbUser = await searchUserInDatabase(cleanUsername);

    if (dbUser && dbUser.searchData) {
      console.log('✅ Found verification user in database:', dbUser.instagramUsername);

      const verifyUser = {
        id: `verify_${dbUser.id}`,
        username: `@${dbUser.instagramUsername}`,
        avatar: dbUser.instagramUsername.charAt(0).toUpperCase(),
        verified: dbUser.searchData.is_verified || false,
        displayName: dbUser.searchData.full_name || `@${dbUser.instagramUsername}`,
        profilePicture: dbUser.searchData.profile_pic_url ?
          `${API_BASE_URL}/api/proxy-image?url=${encodeURIComponent(dbUser.searchData.profile_pic_url)}` :
          null,
        searchData: dbUser.searchData
      };

      // Generate verification code
      const code = generateVerificationCode(dbUser.instagramUsername);

      setReviewData(prev => ({
        ...prev,
        verifyUsername: verifyUser,
        generatedCode: code
      }));

      setLookupError(null);
      return;
    }

    // Check cache for non-demo users
    const cachedUser = getCachedUser(cleanUsername);
    if (cachedUser) {
      console.log('✅ Using cached user for verification:', cleanUsername);

      const verifyUser = {
        id: `verify_${cachedUser.user_id || cleanUsername}`,
        username: `@${cachedUser.username}`,
        avatar: cachedUser.username.charAt(0).toUpperCase(),
        verified: cachedUser.is_verified || false,
        displayName: cachedUser.full_name || `@${cachedUser.username}`,
        profilePicture: cachedUser.profile_pic_url ?
          `${API_BASE_URL}/api/proxy-image?url=${encodeURIComponent(cachedUser.profile_pic_url)}` :
          null,
        searchData: cachedUser
      };

      // Generate verification code
      const code = generateVerificationCode(cachedUser.username);

      setReviewData(prev => ({
        ...prev,
        verifyUsername: verifyUser,
        generatedCode: code
      }));

      setLookupError(null);
      return;
    }

    if (isLookingUpUser) {
      console.log('🔄 Already looking up, skipping duplicate call for:', cleanUsername);
      return;
    }

    const lookupKey = `verify_lookup_${cleanUsername}`;
    if (window[lookupKey] && Date.now() - window[lookupKey] < 5000) {
      console.log('🚫 Recent lookup for same username, skipping:', cleanUsername);
      return;
    }
    window[lookupKey] = Date.now();

    setIsLookingUpUser(true);
    setLookupError(null);

    try {
      console.log('🔍 Looking up user for verification via API:', cleanUsername);

      const searchResult = await fetch(`${API_BASE_URL}/api/apify/instagram-profile-scraper?username=${cleanUsername}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (searchResult.ok) {
        const searchData = await searchResult.json();
        console.log('✅ User verification lookup successful:', searchData);

        if (searchData.success && searchData.data) {
          const isValidUser = searchData.data.user_id &&
            searchData.data.username &&
            searchData.raw_data?.error !== 'not_found' &&
            (searchData.data.followers > 0 || searchData.data.posts > 0 || searchData.data.full_name);

          if (isValidUser) {
            // Cache the user data
            setCachedUser(cleanUsername, searchData.data);

            // Save user to database (async, non-blocking)
            saveUserToDatabase({
              instagramUsername: searchData.data.username,
              source: 'apify_scraper_verification',
              searchData: searchData.data
            }).catch(error => {
              console.warn('Failed to save verification user to database (non-critical):', error);
            });

            const verifyUser = {
              id: `verify_${searchData.data.user_id || cleanUsername}`,
              username: `@${searchData.data.username}`,
              avatar: searchData.data.username.charAt(0).toUpperCase(),
              verified: searchData.data.is_verified || false,
              displayName: searchData.data.full_name || `@${searchData.data.username}`,
              profilePicture: searchData.data.profile_pic_url ?
                `${API_BASE_URL}/api/proxy-image?url=${encodeURIComponent(searchData.data.profile_pic_url)}` :
                null,
              searchData: searchData.data
            };

            // Generate verification code
            const code = generateVerificationCode(searchData.data.username);

            setReviewData(prev => ({
              ...prev,
              verifyUsername: verifyUser,
              generatedCode: code
            }));

            setLookupError(null);
            return;
          }
        }
      }

      setLookupError(`❌ @${cleanUsername} not found on Instagram`);
      setReviewData(prev => ({ ...prev, verifyUsername: null, generatedCode: '' }));

    } catch (error) {
      console.error('❌ Error looking up user for verification:', error);
      setLookupError(`Unable to verify @${cleanUsername} - please try again`);
      setReviewData(prev => ({ ...prev, verifyUsername: null, generatedCode: '' }));
    } finally {
      setIsLookingUpUser(false);
    }
  };

  // Instagram username lookup function with demo user support, database search, and caching
  const handleBusinessLookup = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 1) return;

    // Remove @ if present and clean the search term
    const cleanUsername = searchTerm.replace('@', '').trim();
    const formattedUsername = `@${cleanUsername}`;

    // Check if it's a demo user first
    if (isDemoUser(formattedUsername)) {
      console.log('✅ Using demo user:', formattedUsername);

      const demoUser = demoUsers.find(user => user.username.toLowerCase() === formattedUsername.toLowerCase());
      if (demoUser) {
        // Clear existing business/Instagram users and add the demo user
        setAvailableBusinesses(prev => {
          const otherDemoUsers = demoUsers.filter(user => user.username.toLowerCase() !== formattedUsername.toLowerCase());
          return [demoUser, ...otherDemoUsers];
        });

        setLookupError(null);
        return;
      }
    }

    // Search in database first
    console.log('🔍 Searching in database first:', cleanUsername);
    const dbUser = await getBusinessByUsername(cleanUsername);

    if (dbUser) {
      console.log('✅ Found business in database:', dbUser.instagramUsername);

      // Clear existing business/Instagram users and add the database user
      setAvailableBusinesses(prev => {
        const demoUsers = prev.filter(user => user.source === 'demo');
        return [...demoUsers, dbUser.searchData];
      });

      setLookupError(null);
      return;
    }

    // Prevent duplicate calls for the same username
    if (isLookingUpUser) {
      console.log('🔄 Already looking up, skipping duplicate call for:', cleanUsername);
      return;
    }

    // Create a unique key for this lookup to prevent rapid duplicate calls
    const lookupKey = `lookup_${cleanUsername}`;
    if (window[lookupKey] && Date.now() - window[lookupKey] < 5000) { // 5 seconds
      console.log('🚫 Recent lookup for same username, skipping:', cleanUsername);
      return;
    }
    window[lookupKey] = Date.now();

    setIsLookingUpUser(true);
    setLookupError(null); // Clear any previous errors

    const startTime = Date.now();

    try {
      console.log('🔍 Looking up Instagram account via API:', cleanUsername);

      // Try Apify Instagram scraper (reliable and optimized with caching)
      console.log('🕷️ Trying Apify Instagram scraper...');

      const searchResult = await fetch(`${API_BASE_URL}/api/apify/instagram-profile-scraper?username=${cleanUsername}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (searchResult.ok) {
        const searchData = await searchResult.json();
        const executionTime = Date.now() - startTime;

        console.log(`✅ Apify Instagram scraper response (${executionTime}ms, ${searchData.cached ? 'cached' : 'fresh'}):`, searchData);

        if (searchData.success && searchData.data) {
          // Check if the user actually exists by validating key fields
          const isValidUser = searchData.data.user_id &&
            searchData.data.username &&
            searchData.raw_data?.error !== 'not_found' &&
            (searchData.data.followers > 0 || searchData.data.posts > 0 || searchData.data.full_name);

          if (isValidUser) {
            console.log('✅ Valid Instagram user found:', searchData.data.username);

            // Save user to database (async, non-blocking)
            // This is Business NOt a User 
            // saveBusinessToDatabase(searchData.data).catch(error => {
            //   console.warn('Failed to save user to database (non-critical):', error);
            // });

            // Clear existing business/Instagram users and add the new one
            setAvailableBusinesses(prev => {
              const demoUsers = prev.filter(user => user.source === 'demo');
              return [...demoUsers, searchData.data];
            });

            setLookupError(null);
            return; // Success, exit early
          } else {
            // User doesn't exist - Apify returned empty/invalid data
            console.log('❌ Instagram user not found (empty data):', cleanUsername);

            setLookupError(`❌ @${cleanUsername} not found on Instagram`);

            // Remove Instagram/business users from dropdown, keep only demo users
            setAvailableBusinesses(prev => prev.filter(user => user.source === 'demo'));

            // Clear error after 7 seconds
            setTimeout(() => setLookupError(null), 7000);
            return;
          }
        } else if (!searchData.success) {
          // Handle specific Apify errors
          console.log('❌ Apify error:', searchData);

          let errorMessage = `❌ @${cleanUsername} not found on Instagram`;
          if (searchData.error === 'Invalid username format') {
            errorMessage = `❌ Invalid username format: ${searchData.message}`;
          } else if (searchData.message) {
            errorMessage = `❌ ${searchData.message}`;
          }

          setLookupError(errorMessage);

          // Remove Instagram/business users from dropdown, keep only demo users
          setAvailableBusinesses(prev => prev.filter(user => user.source === 'demo'));

          // Clear error after 7 seconds
          setTimeout(() => setLookupError(null), 7000);
          return;
        }
      } else {
        // Profile not found
        const errorData = await searchResult.json().catch(() => ({ error: 'Unknown error' }));
        console.log('❌ Instagram profile not found:', errorData);

        setLookupError(`❌ @${cleanUsername} not found on Instagram`);

        // Remove Instagram/business users from dropdown, keep only demo users
        setAvailableBusinesses(prev => prev.filter(user => user.source === 'demo'));

        // Clear error after 7 seconds
        setTimeout(() => setLookupError(null), 7000);
        return;
      }

    } catch (error) {
      console.error('❌ Error looking up Instagram user:', error);

      // Set appropriate error message
      setLookupError(`Unable to verify @${cleanUsername} - please try logging in with Instagram`);

      // Remove Instagram/business users from dropdown, keep only demo users
      setAvailableBusinesses(prev => prev.filter(user => user.source === 'demo'));

      // Clear error after 7 seconds
      setTimeout(() => setLookupError(null), 7000);
    } finally {
      setIsLookingUpUser(false);
    }
  };


  const reviewTypes = [
    { id: 'service', name: 'Service', icon: '💼', description: 'Coaching, consulting, courses' },
    { id: 'advertisement', name: 'Advertisement', icon: '📢', description: 'Products, brands, sponsorships' }
  ];

  // // Comprehensive Service Types (fallback if not passed as prop)
  // const defaultServiceTypes = [
  //   // Fitness & Health
  //   { id: 'personal_training', name: 'Personal Training', category: 'fitness', icon: '🏋️', description: 'One-on-one fitness coaching' },
  //   { id: 'group_fitness', name: 'Group Fitness Classes', category: 'fitness', icon: '👥', description: 'Group workout sessions' },
  //   { id: 'nutrition_coaching', name: 'Nutrition Coaching', category: 'fitness', icon: '🥗', description: 'Diet and meal planning' },
  //   { id: 'meal_prep', name: 'Meal Prep Service', category: 'fitness', icon: '🍱', description: 'Prepared healthy meals' },
  //   { id: 'yoga_instruction', name: 'Yoga Instruction', category: 'fitness', icon: '🧘‍♀️', description: 'Yoga classes and poses' },
  //   { id: 'sports_coaching', name: 'Sports Coaching', category: 'fitness', icon: '⚽', description: 'Specific sport training' },

  //   // Business & Marketing
  //   { id: 'business_consulting', name: 'Business Consulting', category: 'business', icon: '💼', description: 'Strategic business advice' },
  //   { id: 'marketing_strategy', name: 'Marketing Strategy', category: 'business', icon: '📊', description: 'Marketing planning and execution' },
  //   { id: 'social_media_management', name: 'Social Media Management', category: 'business', icon: '📱', description: 'Social media content and strategy' },
  //   { id: 'brand_development', name: 'Brand Development', category: 'business', icon: '🎯', description: 'Brand identity and positioning' },
  //   { id: 'sales_coaching', name: 'Sales Coaching', category: 'business', icon: '💰', description: 'Sales skills and techniques' },
  //   { id: 'pitch_deck_review', name: 'Pitch Deck Review', category: 'business', icon: '🚀', description: 'Investor pitch preparation' },
  //   { id: 'financial_planning', name: 'Financial Planning', category: 'business', icon: '📈', description: 'Financial strategy and budgeting' },

  //   // Wellness & Mindfulness
  //   { id: 'life_coaching', name: 'Life Coaching', category: 'wellness', icon: '🌟', description: 'Personal development coaching' },
  //   { id: 'therapy_session', name: 'Therapy Session', category: 'wellness', icon: '🗣️', description: 'Mental health counseling' },
  //   { id: 'meditation_guidance', name: 'Meditation Guidance', category: 'wellness', icon: '🧘', description: 'Meditation techniques and practice' },
  //   { id: 'stress_management', name: 'Stress Management', category: 'wellness', icon: '😌', description: 'Stress reduction techniques' },
  //   { id: 'relationship_counseling', name: 'Relationship Counseling', category: 'wellness', icon: '💕', description: 'Couples and relationship advice' },
  //   { id: 'mindfulness_workshop', name: 'Mindfulness Workshop', category: 'wellness', icon: '🌱', description: 'Mindfulness practice sessions' },

  //   // Creative & Design
  //   { id: 'graphic_design', name: 'Graphic Design', category: 'creative', icon: '🎨', description: 'Visual design and branding' },
  //   { id: 'photography', name: 'Photography Session', category: 'creative', icon: '📸', description: 'Photo shoots and editing' },
  //   { id: 'video_production', name: 'Video Production', category: 'creative', icon: '🎬', description: 'Video creation and editing' },
  //   { id: 'content_creation', name: 'Content Creation', category: 'creative', icon: '✍️', description: 'Written and visual content' },
  //   { id: 'web_design', name: 'Web Design', category: 'creative', icon: '💻', description: 'Website design and development' },
  //   { id: 'illustration', name: 'Illustration', category: 'creative', icon: '🖌️', description: 'Custom artwork and illustrations' },

  //   // Education & Skills
  //   { id: 'tutoring', name: 'Tutoring', category: 'education', icon: '📚', description: 'Academic subject tutoring' },
  //   { id: 'language_lessons', name: 'Language Lessons', category: 'education', icon: '🗣️', description: 'Foreign language instruction' },
  //   { id: 'skill_workshop', name: 'Skill Workshop', category: 'education', icon: '🛠️', description: 'Professional skill development' },
  //   { id: 'career_coaching', name: 'Career Coaching', category: 'education', icon: '🎯', description: 'Career guidance and development' },
  //   { id: 'online_course', name: 'Online Course', category: 'education', icon: '💻', description: 'Structured learning programs' },
  //   { id: 'mentorship', name: 'Mentorship Program', category: 'education', icon: '👥', description: 'Long-term guidance and support' },

  //   // Lifestyle & Beauty
  //   { id: 'makeup_artistry', name: 'Makeup Artistry', category: 'lifestyle', icon: '💄', description: 'Makeup application and lessons' },
  //   { id: 'skincare_consultation', name: 'Skincare Consultation', category: 'lifestyle', icon: '✨', description: 'Personalized skincare advice' },
  //   { id: 'fashion_styling', name: 'Fashion Styling', category: 'lifestyle', icon: '👗', description: 'Personal style and wardrobe' },
  //   { id: 'hair_styling', name: 'Hair Styling', category: 'lifestyle', icon: '💇', description: 'Hair cutting and styling' },
  //   { id: 'lifestyle_coaching', name: 'Lifestyle Coaching', category: 'lifestyle', icon: '🌟', description: 'Overall lifestyle improvement' },
  //   { id: 'wardrobe_consultation', name: 'Wardrobe Consultation', category: 'lifestyle', icon: '👚', description: 'Closet organization and styling' },

  //   // Other/General
  //   { id: 'consultation', name: 'General Consultation', category: 'all', icon: '💬', description: 'General advice and consultation' },
  //   { id: 'custom_service', name: 'Custom Service', category: 'all', icon: '🔧', description: 'Customized service offering' }
  // ];

  // // Use passed serviceTypes or fall back to default
  // const serviceTypes = propServiceTypes || defaultServiceTypes;

  const regions = [
    { id: 'north_america', name: 'North America', icon: '🇺🇸', description: 'United States, Canada, Mexico' },
    { id: 'europe', name: 'Europe', icon: '🇪🇺', description: 'European Union and surrounding countries' },
    { id: 'asia', name: 'Asia', icon: '🌏', description: 'Asia Pacific region' },
    { id: 'south_america', name: 'South America', icon: '🇧🇷', description: 'South American countries' },
    { id: 'other', name: 'Other Regions', icon: '🌍', description: 'Africa, Oceania, and other regions' }
  ];

  const demoVerificationCodes = [
    { code: 'FIT2024', service: '@fitcoach_sarah', valid: true },
    { code: 'BUS2024', service: '@businessmentor_alex', valid: true },
    { code: 'WELL2024', service: '@mindful_maya', valid: true },
    { code: 'EDU2024', service: '@tech_mentor_jake', valid: true }
  ];

  const handleNext = async () => {
    // Save user to database when proceeding from step 1 (Choose Your Profile)
    if (currentStep === 1 && reviewData.user && reviewData.user.source !== 'demo') {
      // Only save non-demo users that aren't already from database
      if (reviewData.user.source !== 'database') {
        // saveUserToDatabase({
        //   instagramUsername: reviewData.user.instagramUsername || reviewData.user.username?.replace('@', ''),
        //   source: reviewData.user.source || 'manual_selection',
        //   searchData: reviewData.user.searchData || {}
        // }).catch(error => {
        //   console.warn('Failed to save selected user to database (non-critical):', error);
        // });
      }
      console.log('📝 User selected for review flow:', reviewData.user.username);
    }

    // Skip verification step (step 7) for authenticated users, go directly to promotion code step
    if (currentStep === 6 && isLoggedIn && safeUserProfile) {
      console.log('🔐 Skipping verification step for authenticated user, going to promotion code step');
      setReviewData(prev => ({
        ...prev,
        isUserVerified: true,
        isVerified: true, // Set verified to true for authenticated users
        verificationMethod: 'authenticated',
        skippedVerification: false,
        verification: {
          isVerified: true,
          method: 'authenticated',
          verificationCode: null,
          skipped: false,
          emailAddress: safeUserProfile.identifiers?.email || '',
          phoneNumber: safeUserProfile.identifiers?.phone || ''
        }
      }));
      setCurrentStep(8); // Go directly to step 8 (promotion code - final step)
      return;
    }

    // NOTE: Review saving moved to handleSubmit() function - no auto-save on step 7

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleVerificationCodeCheck = () => {
    const matchingCode = demoVerificationCodes.find(
      code => code.code === reviewData.verificationCode &&
        code.service === selectedService?.instagramHandle
    );

    if (matchingCode && matchingCode.valid) {
      setReviewData(prev => ({ ...prev, isVerified: true }));
    } else {
      alert('Invalid verification code. Please check with the service provider.');
    }
  };

  const handleSubmit = async () => {
    console.log('🚀 Starting review submission...');
    console.log('🔍 Current review data:', reviewData);
    console.log('🔍 Authentication state:', { isLoggedIn, userProfile: safeUserProfile });

    // Clear any previous errors
    setLookupError(null);

    // Validate required fields with professional error handling
    if (!reviewData.user) {
      setLookupError('Please select a user profile');
      return;
    }
    if (reviewData.rating === 0) {
      setLookupError('Please select a rating');
      return;
    }
    if (!reviewData.reviewType) {
      setLookupError('Please select what you are reviewing');
      return;
    }
    if (!reviewData.serviceType) {
      setLookupError('Please select the specific service type');
      return;
    }
    if (!reviewData.region) {
      setLookupError('Please select your region');
      return;
    }
    if (!reviewData.comment.trim()) {
      setLookupError('Please write a review comment');
      return;
    }

    // For authenticated users, automatically set verification to true
    if (isLoggedIn && safeUserProfile && !reviewData.isVerified) {
      console.log('🔐 Auto-verifying for authenticated user');
      setReviewData(prev => ({
        ...prev,
        isVerified: true,
        isUserVerified: true,
        verificationMethod: 'authenticated'
      }));
    }

    // Check verification (make verification optional - allow submission without strict verification)
    const isVerified = reviewData.isVerified || reviewData.isUserVerified || reviewData.isPhoneVerified || reviewData.isEmailVerified || (isLoggedIn && safeUserProfile);

    // Verification is now optional - users can submit without verification
    // Log verification status for debugging
    console.log('🔍 Verification status:', {
      isVerified,
      isLoggedIn,
      hasUserProfile: !!safeUserProfile,
      reviewDataVerified: reviewData.isVerified,
      userVerified: reviewData.isUserVerified,
      phoneVerified: reviewData.isPhoneVerified,
      emailVerified: reviewData.isEmailVerified
    });

    try {
      console.log('💾 Saving review to backend...');
      setIsSavingReview(true);

      // Save the review using the flattened payload
      const result = await saveCompleteReview();
      console.log('✅ Review saved successfully:', result);

      // For email-verified users, treat them as authenticated and pass authentication data to parent
      const isEmailVerified = reviewData.isEmailVerified && reviewData.emailAddress;

      if (isEmailVerified || reviewData.isPhoneVerified) {
        // Create authenticated user profile from verification data
        const verifiedUserProfile = {
          email: reviewData.emailAddress || reviewData.phoneNumber,
          name: reviewData.emailAddress?.split('@')[0] || 'User',
          verified: true,
          loginMethod: reviewData.isEmailVerified ? 'email_verification' : 'phone_verification',
          identifiers: {
            email: reviewData.emailAddress,
            phone: reviewData.phoneNumber
          },
          verificationStatus: {
            email: reviewData.isEmailVerified,
            phone: reviewData.isPhoneVerified
          }
        };

        setUserProfile(verifiedUserProfile)

        console.log('✅ User verified during review process, treating as authenticated:', verifiedUserProfile);

        // Pass both review data and authentication status to parent
        if (onComplete) {
          onComplete({
            ...reviewData,
            reviewResult: result,
            shouldAuthenticate: true,
            userProfile: verifiedUserProfile,
            authMethod: reviewData.isEmailVerified ? 'email_verification' : 'phone_verification'
          });
        }
      } else {
        // Regular completion without authentication
        if (onComplete) {
          onComplete({
            ...reviewData,
            reviewResult: result
          });
        }
      }

      onClose();
    } catch (error) {
      console.error('❌ Error saving review:', error);

      // Show professional error handling without alerts
      setLookupError(`Review submission failed: ${error.message}`);

      // Clear error after 5 seconds
      setTimeout(() => setLookupError(null), 5000);

      // Don't close the modal on error - let user try again
      console.log('🔄 Keeping review modal open for user to retry');
    } finally {
      setIsSavingReview(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        console.log('Case 1 - reviewData.user:', reviewData.user);
        return reviewData.user !== null; // Choose Your Profile
      case 2: return reviewData.rating > 0; // Rate Your Experience
      case 3: return reviewData.reviewType !== null; // What Are You Reviewing
      case 4: return reviewData.serviceType !== null; // Specific Service Type
      case 5: return reviewData.region !== null; // Your Region
      case 6: return reviewData.comment.trim().length > 0; // Share Your Experience
      case 7: {
        // Verify Me step - check different verification methods
        // For authenticated users, always allow proceeding
        if (isLoggedIn && safeUserProfile) return true;
        if (reviewData.skippedVerification) return true;

        switch (reviewData.verificationMethod) {
          case 'instagram':
            return reviewData.isUserVerified;
          case 'phone':
            return reviewData.isPhoneVerified;
          case 'email':
            return reviewData.isEmailVerified;
          default:
            return false;
        }
      }
      case 8: return true; // Promotion Code (optional, always can proceed)
      default: return false;
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="text-white" size={32} />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">Choose Your Profile</h3>
              <p className="text-gray-600">Select a demo user or search for an Instagram username</p>
            </div>

            <EnhancedUserDropdown
              options={availableBusinesses}
              value={reviewData.user}
              onChange={(user) => {
                setReviewData(prev => ({ ...prev, user }));
                setBusinessService(user);
              }}
              placeholder="Select a user profile or search @username (try: nonexistentuser12345678911)"
              onInstagramLookup={handleBusinessLookup}
              // onInstagramLookup={handleInstagramLookup}
              isLookingUp={isLookingUpUser}
            />

            {isLookingUpUser && (
              <div className="flex items-center justify-center gap-2 text-purple-600 text-sm">
                <Loader className="animate-spin" size={16} />
                <span>Searching Instagram business accounts...</span>
              </div>
            )}

            {lookupError && (
              <div className="flex items-center justify-center gap-2 text-red-600 text-sm bg-red-50 rounded-lg p-3 border border-red-200">
                <AlertTriangle size={16} />
                <span>{lookupError}</span>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            {/* Selected User Display */}
            {businessService && (
              <div className="p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                <div className="flex items-center space-x-4">
                  {/* Profile Picture */}
                  <div className="flex-shrink-0">
                    {businessService.profile_pic_url ? (
                      // <img
                      //   src={`${API_BASE_URL}/api/proxy-image?url=${encodeURIComponent(businessService.profile_pic_url)}`}
                      //   alt={`${businessService.username} profile`}
                      //   className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-md"
                      //   onError={(e) => {
                      //     e.target.style.display = 'none';
                      //     e.target.nextSibling.style.display = 'flex';
                      //   }}

                      <ProfileImage
                        profilePicture={
                          businessService.profile_pic_url
                        }
                        profile_pic_base64={businessService.profile_pic_base64}
                        altName={`${businessService.username} profile`}
                        className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-md"
                      />
                    ) : null}
                    <div
                      className={`w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-xl shadow-md ${businessService.profile_pic_url ? 'hidden' : 'flex'}`}
                    >
                      {businessService.avatar}
                    </div>
                  </div>

                  {/* User Info */}
                  <div className="flex-grow">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="text-lg font-bold text-gray-900">{businessService.username}</h4>
                      {(businessService.verified || businessService.is_verified) && (
                        <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>

                    {/* Full Name */}
                    {businessService.full_name && (
                      <p className="text-gray-700 font-medium mb-1">{businessService.full_name}</p>
                    )}

                    {/* Biography - truncated for this step */}
                    {businessService.biography && (
                      <p className="text-gray-600 text-sm mb-2 line-clamp-1">{businessService.biography}</p>
                    )}

                    {/* Stats */}
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      {businessService.followers && (
                        <span><strong>{businessService.followers.toLocaleString()}</strong> followers</span>
                      )}
                      {businessService.posts && (
                        <span><strong>{businessService.posts.toLocaleString()}</strong> posts</span>
                      )}
                      {(businessService.searchData?.is_business_account || businessService.is_business_account) && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Business
                        </span>
                      )}
                      {(businessService.is_private || businessService.is_private) && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Private
                        </span>
                      )}
                      {(businessService.is_professional_account || businessService.is_professional_account) && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Professional
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <Star className="text-white" size={20} />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">Rate Your Experience</h3>
              <p className="text-gray-600 text-sm sm:text-base px-4 sm:px-0">How would you rate {businessService?.username} {selectedService?.serviceName}?</p>
            </div>

            <div className="flex justify-center gap-1 sm:gap-2">
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  onClick={() => setReviewData(prev => ({ ...prev, rating: star }))}
                  className={`p-2 sm:p-3 rounded-lg transition-all ${star <= reviewData.rating
                    ? 'bg-yellow-100 text-yellow-600'
                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                    }`}
                >
                  <Star
                    size={24}
                    className={`sm:w-8 sm:h-8 ${star <= reviewData.rating ? 'fill-current' : ''}`}
                  />
                </button>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500">
              {reviewData.rating > 0 ? `${reviewData.rating} out of 5 stars` : 'Select your rating'}
            </p>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            {/* Selected User Display */}
            {businessService && (
              <div className="p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                <div className="flex items-center space-x-4">
                  {/* Profile Picture */}
                  <div className="flex-shrink-0">
                    {businessService.profile_pic_url ? (
                      // <img
                      //   src={`${API_BASE_URL}/api/proxy-image?url=${encodeURIComponent(businessService.profile_pic_url)}`}
                      //   alt={`${businessService.username} profile`}
                      //   className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-md"
                      //   onError={(e) => {
                      //     e.target.style.display = 'none';
                      //     e.target.nextSibling.style.display = 'flex';
                      //   }}
                      // />

                      <ProfileImage
                        profilePicture={
                          businessService.profile_pic_url
                        }
                        profile_pic_base64={businessService.profile_pic_base64}
                        altName={`${businessService.username} profile`}
                        className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-md"
                      />
                    ) : null}
                    <div
                      className={`w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-xl shadow-md ${businessService.profile_pic_url ? 'hidden' : 'flex'}`}
                    >
                      {businessService.avatar}
                    </div>
                  </div>

                  {/* User Info */}
                  <div className="flex-grow">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="text-lg font-bold text-gray-900">{businessService.username}</h4>
                      {(businessService.verified || businessService.is_verified) && (
                        <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>

                    {/* Full Name */}
                    {businessService.full_name && (
                      <p className="text-gray-700 font-medium mb-1">{businessService.full_name}</p>
                    )}

                    {/* Biography - truncated for this step */}
                    {businessService.biography && (
                      <p className="text-gray-600 text-sm mb-2 line-clamp-1">{businessService.biography}</p>
                    )}

                    {/* Stats */}
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      {businessService.followers && (
                        <span><strong>{businessService.followers.toLocaleString()}</strong> followers</span>
                      )}
                      {businessService.posts && (
                        <span><strong>{businessService.posts.toLocaleString()}</strong> posts</span>
                      )}
                      {(businessService.searchData?.is_business_account || businessService.is_business_account) && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Business
                        </span>
                      )}
                      {(businessService.is_private || businessService.is_private) && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Private
                        </span>
                      )}
                      {(businessService.is_professional_account || businessService.is_professional_account) && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Professional
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <span className="text-white text-xl sm:text-2xl">📝</span>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">What Are You Reviewing?</h3>
              <p className="text-gray-600 text-sm sm:text-base px-4 sm:px-0">Choose the type of experience you're reviewing</p>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:gap-3">
              {reviewTypes.map(type => (
                <button
                  key={type.id}
                  onClick={() => setReviewData(prev => ({ ...prev, reviewType: type }))}
                  className={`p-3 sm:p-4 border-2 rounded-lg transition-all text-left ${reviewData.reviewType?.id === type.id
                    ? 'border-purple-500 bg-purple-50 text-purple-700'
                    : 'border-gray-200 hover:border-gray-300'
                    }`}
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <span className="text-xl sm:text-2xl">{type.icon}</span>
                    <div>
                      <p className="font-semibold text-sm sm:text-base">{type.name}</p>
                      <p className="text-xs sm:text-sm text-gray-500">{type.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <span className="text-white text-xl sm:text-2xl">🛠️</span>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">Specific Service Type</h3>
              <p className="text-gray-600 text-sm sm:text-base px-4 sm:px-0">What specific service did you receive?</p>
            </div>

            <OptimizedDropdown
              options={serviceTypes}
              value={reviewData.serviceType}
              onChange={(serviceType) => setReviewData(prev => ({ ...prev, serviceType }))}
              placeholder="Select the service you received"
              searchable={true}
            />
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-blue-500 to-green-500 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <span className="text-white text-xl sm:text-2xl">🌍</span>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">Your Region</h3>
              <p className="text-gray-600 text-sm sm:text-base px-4 sm:px-0">Select your geographic location</p>
            </div>

            <OptimizedDropdown
              options={regions}
              value={reviewData.region}
              onChange={(region) => setReviewData(prev => ({ ...prev, region }))}
              placeholder="Select your region"
              defaultValue={regions[0]}
              searchable={true}
            />
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-green-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <span className="text-white text-xl sm:text-2xl">✍️</span>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">Share Your Experience</h3>
              <p className="text-gray-600 text-sm sm:text-base px-4 sm:px-0">Tell others about your experience with {selectedService?.serviceName}</p>
            </div>

            <textarea
              value={reviewData.comment}
              onChange={(e) => setReviewData(prev => ({ ...prev, comment: e.target.value }))}
              placeholder="Share your detailed experience..."
              className="w-full p-3 sm:p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base"
              rows={3}
            />
            <p className="text-sm text-gray-500 text-center">
              {reviewData.comment.length} characters
            </p>
          </div>
        );

      case 7:
        // If user is authenticated, show verified status instead of verification form
        if (isLoggedIn && safeUserProfile) {
          return (
            <div className="space-y-6">
              {/* <pre>{JSON.stringify(safeUserProfile, null, 2)}</pre> */}
              <pre>{JSON.stringify(businessService, null, 2)}</pre>
              {/* <pre>{JSON.stringify(reviewData, null, 2)}</pre> */}
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="text-white" size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Verified User</h3>
                <p className="text-gray-600">You are logged in and verified to submit reviews</p>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="text-green-600" size={20} />
                  <div>
                    <h4 className="font-semibold text-green-800">Authentication Verified</h4>
                    <p className="text-sm text-green-700">
                      Logged in as: {(() => {
                        try {
                          console.log('🔍 Rendering user info, safeUserProfile:', safeUserProfile);
                          const email = safeUserProfile?.identifiers?.email;
                          const username = safeUserProfile?.identifiers?.instagramUsername;

                          console.log('🔍 Email value:', email, 'Type:', typeof email);
                          console.log('🔍 Username value:', username, 'Type:', typeof username);

                          if (typeof email === 'string' && email) return email;
                          if (typeof username === 'string' && username) return username;

                          // Check other identifier keys
                          const identifiers = safeUserProfile?.identifiers || {};
                          for (const [key, value] of Object.entries(identifiers)) {
                            if (typeof value === 'string' && value) {
                              console.log('🔍 Using identifier:', key, '=', value);
                              return value;
                            }
                          }

                          return 'User';
                        } catch (error) {
                          console.error('Error rendering user profile:', error);
                          return 'User';
                        }
                      })()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          );
        }

        // Use the new VerificationSteps component
        return (
          <VerificationSteps
            verificationMethod={reviewData.verificationMethod}
            setVerificationMethod={method => setReviewData(prev => ({ ...prev, verificationMethod: method }))}
            verifyUsername={reviewData.verifyUsername}
            setVerifyUsername={user => setReviewData(prev => ({ ...prev, verifyUsername: user }))}
            generatedCode={reviewData.generatedCode}
            setGeneratedCode={code => setReviewData(prev => ({ ...prev, generatedCode: code }))}
            isUserVerified={reviewData.isUserVerified}
            setIsUserVerified={flag => setReviewData(prev => ({ ...prev, isUserVerified: flag }))}
            phoneNumber={reviewData.phoneNumber}
            setPhoneNumber={num => setReviewData(prev => ({ ...prev, phoneNumber: num }))}
            phoneVerificationCode={reviewData.phoneVerificationCode}
            setPhoneVerificationCode={code => setReviewData(prev => ({ ...prev, phoneVerificationCode: code }))}
            isPhoneVerified={reviewData.isPhoneVerified}
            setIsPhoneVerified={flag => setReviewData(prev => ({ ...prev, isPhoneVerified: flag }))}
            emailAddress={reviewData.emailAddress}
            setEmailAddress={email => setReviewData(prev => ({ ...prev, emailAddress: email }))}
            emailVerificationCode={reviewData.emailVerificationCode}
            setEmailVerificationCode={code => setReviewData(prev => ({ ...prev, emailVerificationCode: code }))}
            isEmailVerified={reviewData.isEmailVerified}
            setIsEmailVerified={flag => setReviewData(prev => ({ ...prev, isEmailVerified: flag }))}
            skippedVerification={reviewData.skippedVerification}
            setSkippedVerification={flag => setReviewData(prev => ({ ...prev, skippedVerification: flag }))}
            isVerified={reviewData.isVerified}
            setIsVerified={flag => setReviewData(prev => ({ ...prev, isVerified: flag }))}
            userProfile={userProfile}
            setUserProfile={setUserProfile}
            lookupError={lookupError}
            setLookupError={setLookupError}
            isLookingUpUser={isLookingUpUser}
            setIsLookingUpUser={setIsLookingUpUser}
            // sendPhoneVerificationCode={sendPhoneVerificationCode}
            // verifyPhoneCode={verifyPhoneCode}
            // sendEmailVerificationCode={sendEmailVerificationCode}
            // verifyEmailCode={verifyEmailCode}
            handleUserVerificationLookup={handleUserVerificationLookup}
            fetchUserReviews={() => { }} // Optional, pass if you use it
            // resetVerificationState={() => { }} // Optional, pass if you use it
            setShowVerifyModal={() => { }} // Optional, pass if you use it
            setAuthError={() => { }} // Optional, pass if you use it
            isSavingReview={isSavingReview}
            reviewSaveSuccess={reviewSaveSuccess}
            setVerificationNotification={setVerificationNotification}
          />
        );

      case 8:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Promotion Code</h3>
              <p className="text-gray-600">Enter your promotion code if you have one (optional)</p>
            </div>

            <div className="space-y-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={reviewData.promotionCode || ''}
                  onChange={(e) => setReviewData(prev => ({ ...prev, promotionCode: e.target.value.toUpperCase() }))}
                  placeholder="Enter promotion code (optional)"
                  className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {reviewData.promotionCode && (
                <div className="flex items-center gap-2 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <Star className="text-purple-600" size={20} />
                  <span className="text-purple-700 font-medium">Promotion code: {reviewData.promotionCode}</span>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">💡 About Promotion Codes</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Promotion codes are provided by service providers</li>
                  <li>• They help track which reviews came from specific campaigns</li>
                  <li>• You can submit your review without a promotion code</li>
                  <li>• If you have one, enter it to support the service provider</li>
                </ul>
              </div>

              {/* Show verification status for authenticated users */}
              {isLoggedIn && safeUserProfile && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="text-green-600" size={20} />
                    <div>
                      <h4 className="font-semibold text-green-800">Verified User</h4>
                      <p className="text-sm text-green-700">
                        Authenticated as: {(() => {
                          try {
                            console.log('🔍 Rendering auth info, safeUserProfile:', safeUserProfile);
                            const email = safeUserProfile?.identifiers?.email;
                            const username = safeUserProfile?.identifiers?.instagramUsername;

                            console.log('🔍 Email value:', email, 'Type:', typeof email);
                            console.log('🔍 Username value:', username, 'Type:', typeof username);

                            if (typeof email === 'string' && email) return email;
                            if (typeof username === 'string' && username) return username;

                            // Check other identifier keys
                            const identifiers = safeUserProfile?.identifiers || {};
                            for (const [key, value] of Object.entries(identifiers)) {
                              if (typeof value === 'string' && value) {
                                console.log('🔍 Using identifier:', key, '=', value);
                                return value;
                              }
                            }

                            return 'User';
                          } catch (error) {
                            console.error('Error rendering user profile:', error);
                            return 'User';
                          }
                        })()}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-lg sm:rounded-2xl shadow-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto my-4 sm:my-0">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Write a Review</h2>
              <p className="text-gray-600 mt-1 text-sm sm:text-base">Step {currentStep} of {totalSteps}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <X size={20} className="sm:w-6 sm:h-6" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          {renderStep()}
        </div>

        {/* Navigation */}
        <div className="p-4 sm:p-6 border-t bg-gray-50">
          {/* Error Display */}
          {lookupError && (
            <div className="mb-4 flex items-center justify-center gap-2 text-red-600 text-sm bg-red-50 rounded-lg p-3 border border-red-200">
              <AlertTriangle size={16} />
              <span>{lookupError}</span>
            </div>
          )}

          <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-0">
            <button
              onClick={handleBack}
              disabled={currentStep === 1 || isSavingReview}
              className="flex items-center justify-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base order-2 sm:order-1"
            >
              <ArrowLeft size={14} className="sm:w-4 sm:h-4" />
              Back
            </button>

            {/* Button logic: 
                - Show "Next" for steps 1-7
                - Show "Submit Review" for step 8 (promotion code - final step)
            */}
            {currentStep < totalSteps ? (
              <button
                onClick={handleNext}
                disabled={!canProceed() || isSavingReview}
                className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base order-1 sm:order-2"
              >
                Next
                <ArrowRight size={14} className="sm:w-4 sm:h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!canProceed() || isSavingReview}
                className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base order-1 sm:order-2"
              >
                {isSavingReview ? (
                  <>
                    <Loader className="animate-spin w-3.5 h-3.5 sm:w-4 sm:h-4" size={14} />
                    <span className="hidden sm:inline">Submitting...</span>
                    <span className="sm:hidden">Submitting...</span>
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">Submit Review</span>
                    <span className="sm:hidden">Submit</span>
                    <CheckCircle size={14} className="sm:w-4 sm:h-4" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};